import React from 'react';

const Example = (props) => (
  <details>
    <summary>{props.example.title}</summary>
    <div className="example">
      <dl>
        {props.example.response_defs &&
          Object.entries(props.example.response_defs).map(([name, value]) => (
            <React.Fragment key={name}>
              <dt>{name}:</dt>
              <dd>{value}.</dd>
            </React.Fragment>
          ))}
      </dl>
      <pre>
        <code>{props.example.request}</code>
        <samp>{props.example.response}</samp>
      </pre>
    </div>
  </details>
);

const noScope = [
  {
    title: 'API status',
    request: 'curl https://api.onboarddata.io',
    response: '{"status": "ok"}',
  },
  {
    title: 'All available resources',
    request: 'curl https://api.onboarddata.io/swagger',
    response: `{
  "info": {
  "description": "Onboard Data's building sensor data API. Resources are grouped by their API scope and can be accessed by an API key with matching permissions.",
  "title": "OnboardData",
  "version": "1.1.0"
  },
  ...
}`,
  },
];

const generalScope = [
  {
    title: 'All equipment types',
    response_defs: {
      id: 'An integer that identifies the equipment type uniquely',
      tag_name: 'The name of the tag associated with the equipment type',
      name_long: 'The human readable name of the equipment type',
      name_abbr: 'The abbreviated name of the equipment type',
      active: 'true if the equipment type can be tagged, false otherwise',
      flow_order: 'The hierarchy of equipment from the plant to terminal',
      tags: 'The tags associated with the equipment type',
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
https://api.onboarddata.io/equiptype`,
    response: `[
  {
    "id": 12,
    "tag_name": "ahu",
    "name_long": "Air Handling Unit",
    "name_abbr": "AHU",
    "active": true,
    "flow_order": 2,
    "tags": ["hvac", "ahu"]
  },
  ...
]`,
  },
  {
    title: 'All point types',
    response_defs: {
      id: 'An integer that identifies the point type uniquely',
      tag_name: 'The human readable name of the point type',
      display_name: 'The same as tag_name',
      active: 'true if the point type can be tagged, false otherwise',
      measurement_id: 'The ID of the measurement associated with the point type',
      tag_set_ids: 'The IDs of the tags associated with the point type',
      tags: 'The tags associated with the point type',
      default_unit_id: 'The ID of the default unit of the point type',
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
https://api.onboarddata.io/pointtypes`,
    response: `[
  {
    "id": 124,
    "tag_name": "Occupied Heating Setpoint",
    "display_name": "Occupied Heating Setpoint",
    "active": true,
    "measurement_id": 1,
    "tag_set_ids": [15, 2, 4, 62, 51, 55],
    "tags": ["air", "sp", "temp", "zone", "heating", "occ"],
    "default_unit_id": null
  },
  ...
]`,
  },
  {
    title: 'All tags',
    response_defs: {
      id: 'An integer that identifies the tag uniquely',
      name: 'The human readable name of the tag',
      definition: 'The definition of the tag',
      def_source: "The source of the tag's definition",
      def_url: "The URL of the tag's definition",
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
https://api.onboarddata.io/tags`,
    response: `[
  {
    "id": 120,
    "name": "battery",
    "definition": "A container that stores chemical energy that can be converted into electricity and used as a source of power.",
    "def_source": "brick",
    "def_url": "https://brickschema.org/ontology/1.1/classes/Battery/"
  },
  ...
]`,
  },
  {
    title: 'All units',
    response_defs: {
      id: 'An integer that identifies the unit uniquely',
      name_long: 'The human readable name of the unit',
      name_abbr: 'The abbreviated name of the unit',
      data_type: 'The type of data associated with the unit',
      qudt: 'The Quantity, Unit, Dimension and Type (QUDT) reference of the unit',
      unit_type: 'The QUDT reference for the quantity kind associated with the unit',
      raw_encoding: 'Raw strings that are accepted for binary type units',
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
https://api.onboarddata.io/unit`,
    response: `[
  {
    "id": 15,
    "name_long": "British Thermal Unit/hour",
    "name_abbr": "BTU/hr",
    "data_type": "Continuous",
    "raw_encoding": null,
    "display_encoding": null,
    "qudt": "http://qudt.org/vocab/unit/BTU_IT-PER-HR",
    "unit_type": "http://qudt.org/vocab/quantitykind/Power"
  },
  ...
]`,
  },
  {
    title: 'All measurements',
    response_defs: {
      id: 'An integer that identifies the measurement uniquely',
      name: 'The human readable name of the measurement',
      default_unit_id: 'The ID of the default unit of the measurement',
      units_convertible:
        'true if the measurement is convertible to other units associated with it, false otherwise',
      units: 'The units associated with the measurement',
      qudt_type: 'The QUDT reference to the quantity kind associated with the measurement',
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
https://api.onboarddata.io/measurements`,
    response: `[
  {
    "id": 5,
    "name": "Water Flow",
    "default_unit_id": null,
    "units_convertible": false,
    "units": [
      {
        "id": 8,
        "name_long": "Gallons per Minute",
        "name_abbr": "gpm",
        "data_type": "Continuous",
        "raw_encoding": null,
        "display_encoding": null,
        "qudt": "http://qudt.org/vocab/unit/GAL_UK-PER-MIN",
        "unit_type": "http://qudt.org/vocab/quantitykind/VolumeFlowRate"
      },
      {
        "id": 12,
        "name_long": "Milliampere",
        "name_abbr": "mA",
        "data_type": "Continuous",
        "raw_encoding": null,
        "display_encoding": null,
        "qudt": "http://qudt.org/vocab/unit/MilliA",
        "unit_type": "http://qudt.org/vocab/quantitykind/ElectricCurrent"
      },
      {
        "id": 57,
        "name_long": "Cubic Meter per Second",
        "name_abbr": "m3/s",
        "data_type": "Continuous",
        "raw_encoding": null,
        "display_encoding": null,
        "qudt": "http://qudt.org/vocab/unit/M3-PER-SEC",
        "unit_type": "http://qudt.org/vocab/quantitykind/VolumePerUnitTime"
      },
      {
        "id": 58,
        "name_long": "Litre Per Second",
        "name_abbr": "L/s",
        "data_type": "Continuous",
        "raw_encoding": null,
        "display_encoding": null,
        "qudt": "http://qudt.org/vocab/unit/L-PER-SEC",
        "unit_type": "http://qudt.org/vocab/quantitykind/VolumeFlowRate"
      }
    ],
    "qudt_type": null
  },
  ...
]`,
  },
];

const buildingsReadScope = [
  {
    title: 'All the buildings in your organization',
    response_defs: {
      id: 'An integer that identifies the building uniquely',
      org_id: 'The ID of the organization the building belongs to',
      name: 'The name of the building',
      address: 'The address of the building',
      purpose: 'The purpose of the building',
      area: 'The area of the building',
      area_unit: 'The area unit type of the building',
      area_unit_id: 'The area unit id of the building',
      equip_count: 'The total number of equipment identified',
      point_count: 'The total number of points identified',
      timezone: 'The time zone of the location of the building',
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
https://api.onboarddata.io/buildings`,
    response: `[
    {
      "id": 66,
      "org_id": 6,
      "name": "Example Building",
      "address": null,
      "purpose": null,
      "area": null,
      "area_unit_id": 100,
      "area_unit": "ft2",
      "equip_count": 12,
      "point_count": 81,
      "timezone": "America/New_York",
      "info": null
    }
]`,
  },
  {
    title:
      'A particular building in your organization using the value of "id" from one of the objects returned by /buildings',
    response_defs: {
      id: 'An integer that identifies the building uniquely',
      org_id: 'The ID of the organization the building belongs to',
      name: 'The name of the building',
      address: 'The address of the building',
      purpose: 'The purpose of the building',
      area: 'The area of the building',
      area_unit: 'The area unit type of the building',
      area_unit_id: 'The area unit id of the building',
      equip_count: 'The total number of equipment identified',
      point_count: 'The total number of points identified',
      timezone: 'The time zone of the location of the building',
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
https://api.onboarddata.io/buildings/66`,
    response: `{
    "id": 66,
    "org_id": 6,
    "name": "Example Building",
    "address": null,
    "purpose": null,
    "area": null,
    "area_unit_id": 100,
    "area_unit": "ft2",
    "equip_count": 12,
    "point_count": 81,
    "timezone": "America/New_York",
    "info": null
}`,
  },
  {
    title: 'The equipment and points in a building',
    response_defs: {
      id: 'An integer that identifies the equipment instance in a building',
      building_id: 'An integer that identifies the building which houses the equipment',
      equip_id: 'A label given to the equipment instance',
      name: 'A name given to the equipment instance to distinguish it from other equipment of the same type',
      equip_type_name:
        'A human readable name for the equipment type associated with the equipment instance',
      equip_type_id: 'An integer that identifies the equipment type uniquely',
      equip_type_abbr: 'An abbreviation of the equipment type',
      equip_type_tag: 'A name for the equipment type',
      flow_order: 'The hierarchy of equipment from the plant to terminal',
      floor_num_physical: 'The floor number where the equipment is stationed',
      floor_num_served: 'The floor number served by the equipment',
      area_served_desc: 'The area served by the equipment',
      equip_dis: 'A description of the equipment instance',
      points: 'The data points associated with the equipment instance',
    },
    request: `curl -H "X-OB-Api: $ONBOARD_API_KEY_SANDBOX" \\
https://api.onboarddata.io/buildings/66/equipment?points=true`,
    response: `[
    {
      "id": 14719,
      "building_id": 66,
      "equip_id": "AHU-1",
      "suffix": "1_513312",
      "equip_type_name": "Air Handling Unit",
      "equip_type_id": 12,
      "equip_type_abbr": "AHU",
      "equip_type_tag": "ahu",
      "flow_order": 2,
      "floor_num_physical": 1001,
      "floor_num_served": 1,
      "area_served_desc": null,
      "equip_dis": null,
      "points": [
        {
          "id": 162757,
          "building_id": 66,
          "last_updated": 1622048401955.0,
          "first_updated": 1589316904602.0,
          "device": "ACF-1",
          "network_device": null,
          "objectId": "AI:13",
          "name": "SC SP-1",
          "description": "stat_pressure_1",
          "units": "in W.C.",
          "tagged_units": null,
          "raw_unit_id": 4,
          "value": "0.69",
          "type": "Supply Air Static Pressure",
          "point_type_id": 423,
          "measurement_id": 37,
          "topic": "onboard/sandbox/654204/analogInput/13",
          "state_text": null,
          "equip_id": 14719
        },
        ...
      ],
      "tags": ["pump", "hvac"]
    }
]`,
  },
  {
    title: 'The points in a building',
    response_defs: {
      id: 'An integer that identifies the data point uniquely',
      building_id: 'An integer that identifies the building which houses the equipment',
      last_updated:
        'A time stamp that indicates when the latest value for the data point was recorded',
      device: 'The name of the controller device through which the data point is mapped',
      network_device:
        'The IP address of the controller device used to communicate with external devices',
      objectId:
        'The ID associated with the data point that distinguishes it from other data points mapped to the controller device',
      name: 'The BAS object name given to the data point',
      description: 'A detailed description of the data point',
      units: 'The raw unit that is tagged with the data point',
      tagged_units:
        'The unit tagged for the data point when the raw unit from BACnet data must be preserved',
      raw_unit_id: 'The ID of the raw unit associated with the data point',
      value: 'The latest value recorded for the data point',
      type: 'The human readable name for the point type associated with the data point',
      point_type_id: 'An integer that identifies the point type uniquely',
      measurement_id: 'The ID of the measurement associated with the point type',
      topic:
        'A unique string that identifies the data point and is used to match with the corresponding time series data',
      equip_id: 'The ID of the equipment instance associated with the data point',
      state_texts:
        'Strings that dictate the accepted values for a multi-state point type (e.g. ["High","Medium","Low"] are the accepted values for fan modes)',
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
https://api.onboarddata.io/buildings/66/points`,
    response: `[
    {
      "id": 162748,
      "building_id": 66,
      "last_updated": 1621430101959.0,
      "first_updated": 1589316904602.0,
      "device": "ACF-1",
      "network_device": null,
      "objectId": "AI:3",
      "name": "DAT T-4",
      "description": "dat_1",
      "units": "F",
      "tagged_units": null,
      "raw_unit_id": 2,
      "value": "63.79",
      "type": "Supply Air Temperature",
      "point_type_id": 425,
      "measurement_id": 1,
      "topic": "onboard/sandbox/654204/analogInput/3",
      "state_text": null,
      "equip_id": 14719
    },
    ...
]`,
  },
  {
    title: 'The Supply Air Temperature values in a building from 8 am until 9 am on May 1, 2021',
    response_defs: {
      point_id: 'An integer that identifies the data point associated with the sensor data',
      columns: 'The labels of the query results',
      values: 'The query results for the sensor data',
      raw: 'The raw value ingested from the time series data',
      unit: 'The default unit if present, otherwise the raw value',
    },
    request: `curl -H "X-OB-Api: $API_KEY" \\
-H "Content-Type: application/json" \\
-d '{"start": "2021-05-01T08:00:00Z", "end": "2021-05-01T09:00:00Z", "point_ids": [162748]}' \\
https://api.onboarddata.io/query-v2`,
    response: `[
    {
      "point_id":162748,
      "columns":["time","raw","F"],
      "values":[
        ["2021-05-01T08:00:01.972000Z","61.599998474121094",61.599998474121094],
        ["2021-05-01T08:05:02.216000Z","61.5",61.5],
        ["2021-05-01T08:10:02.019000Z","61.599998474121094",61.599998474121094],
        ["2021-05-01T08:15:01.988000Z","61.599998474121094",61.599998474121094],
        ["2021-05-01T08:20:02.054000Z","61.900001525878906",61.900001525878906],
        ["2021-05-01T08:25:02.123000Z","61.79999923706055",61.79999923706055],
        ["2021-05-01T08:30:01.987000Z","61.599998474121094",61.599998474121094],
        ["2021-05-01T08:35:01.955000Z","61.599998474121094",61.599998474121094],
        ["2021-05-01T08:40:02.087000Z","61.70000076293945",61.70000076293945],
        ["2021-05-01T08:45:02.053000Z","61.79999923706055",61.79999923706055],
        ["2021-05-01T08:50:02.027000Z","61.900001525878906",61.900001525878906],
        ["2021-05-01T08:55:01.979000Z","61.79999923706055",61.79999923706055]
      ],
      "raw":"F",
      "unit":"F"
    }
]`,
  },
];

const GettingStarted = () => (
  <article className="getting-started">
    <h1>Getting started with the Onboard API</h1>
    {noScope.map((example) => (
      <Example key={example.title} example={example} />
    ))}
    <details>
      <summary>Authenticate</summary>
      <p>
        Onboard&apos;s resources are grouped by scope and defined at{' '}
        <a href="https://portal.onboarddata.io/docs/swagger">
          https://portal.onboarddata.io/docs/swagger
        </a>
        .
      </p>
      <ol>
        <li>
          Log in to{' '}
          <a href="https://portal.onboarddata.io/account">https://portal.onboarddata.io/account</a>
        </li>
        <li>
          Create an API key with the scopes that correspond to the resources you want to access
        </li>
        <li>Include that key in your requests to the API</li>
      </ol>
    </details>
    <details>
      <summary>
        Endpoints in the <strong>General Scope</strong> return data about the Onboard data model
      </summary>
      {generalScope.map((example) => (
        <Example key={example.title} example={example} />
      ))}
    </details>
    <details>
      <summary>
        Endpoints in the <strong>Buildings Read Scope</strong> return data about the buildings in
        your organization
      </summary>
      {buildingsReadScope.map((example) => (
        <Example key={example.title} example={example} />
      ))}
    </details>
  </article>
);

export default GettingStarted;
